import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { Tooltip } from 'shared/components';
import { ScheduleTooltipContent } from 'src/dispensary-admin/components/schedule-tooltip-content';
import { ScheduleCalendarIcon } from 'src/dispensary-admin/components/schedule-calendar-icon';
import { GqlImageBanner } from 'types/graphql';
import { useUI } from 'src/hooks/use-ui';

export const ListItemScheduleInfo = ({
  schedule,
  isLive,
  isActive,
}: {
  schedule?: GqlImageBanner['schedule'];
  isLive: boolean;
  isActive: boolean;
}): JSX.Element | null => {
  const UI = useUI();
  const dispensaryTimezone = UI.dispensary.timezone ?? moment.tz.guess();

  const [showTooltip, setShowTooltip] = useState(false);

  if (!schedule) {
    return null;
  }

  const startMoment = moment.tz(schedule.startDate, dispensaryTimezone);
  const endMoment = schedule.endDate ? moment.tz(schedule.endDate, dispensaryTimezone) : null;

  const startDateDisplayString = `${startMoment.format('MM/DD/YYYY')} - ${startMoment.format(
    'h:mm A'
  )} (${startMoment.zoneAbbr()})`;
  const endDateDisplayString = endMoment
    ? `${endMoment.format('MM/DD/YYYY')} - ${endMoment.format('h:mm A')} (${endMoment.zoneAbbr()})`
    : 'N/A';

  const isInactive = isLive && !isActive;

  return (
    <ScheduleContainer>
      <TooltipWrapper onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
        <Tooltip
          hideIcon
          tooltipCopy={<ScheduleTooltipContent schedule={schedule} hideStartEndDate />}
          visible={showTooltip}
          left='-30px'
          width='180px'
          top='auto'
          bottom='14px'
          position='absolute'
          centerArrow
        />
        {getCalendarIcon(isLive, isInactive)}
        <ScheduleInfo>
          {!isLive && <ScheduleField label='Scheduled To Start:' value={startDateDisplayString} />}
          <ScheduleField label='Ends:' value={endDateDisplayString} />
          {isInactive && <InactiveScheduleInfo>Inactive for the current day or time</InactiveScheduleInfo>}
        </ScheduleInfo>
      </TooltipWrapper>
    </ScheduleContainer>
  );
};

const getCalendarIcon = (isLive: boolean, isInactive: boolean): JSX.Element => {
  if (isInactive) {
    return <ScheduleCalendarIcon variant='inactive' />;
  }
  if (isLive) {
    return <ScheduleCalendarIcon variant='with-check' />;
  }
  return <ScheduleCalendarIcon variant='default' />;
};

export const ScheduleField = ({ label, value }: { label: string; value: string }): JSX.Element => (
  <ScheduleFieldWrapper>
    <span>{label}</span>
    <span>{value || 'N/A'}</span>
  </ScheduleFieldWrapper>
);

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;

const ScheduleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  color: #6d747b;
`;

const ScheduleInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: end;
  gap: 16px;
  font-size: 12px;
`;

const ScheduleFieldWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;

  > span:last-child {
    font-weight: bold;
  }
`;

const InactiveScheduleInfo = styled.div`
  color: #e46767;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
`;
